import { Autocomplete } from '@base-ui/react/autocomplete';
import { RiArrowDownSLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { dropdownItem, dropdownSurface, fieldBoxStyles, popupMotion, useFieldControlProps } from '@/shared/ui';

/**
 * Free-text field with suggestions, styled as the product select (h32, r8, White/10, 400 13/18, trailing
 * arrow-down-s-line 16 — 586:15927 "QURILMA NOMLANISHINI TANLANG", 586:16515 "O'LCHOV BIRLIGINI TANLANG").
 * The value is whatever is typed; picking a suggestion fills it in. Built on Base UI Autocomplete (missing in the kit).
 */
export function ComboInput({
  value,
  onValueChange,
  options,
  placeholder,
  id,
  invalid,
  disabled,
  maxLength,
  autoFocus,
  'aria-label': ariaLabel,
  className,
  listLabel,
}: {
  value: string;
  onValueChange: (value: string) => void;
  options: readonly string[];
  placeholder?: string;
  id?: string;
  invalid?: boolean;
  disabled?: boolean;
  maxLength?: number;
  autoFocus?: boolean;
  'aria-label'?: string;
  className?: string;
  /** caption above the suggestions */
  listLabel?: string;
}) {
  const field = useFieldControlProps({ id, disabled, invalid });
  const s = fieldBoxStyles({ size: 'md', invalid: field.invalid });
  return (
    <Autocomplete.Root items={options as string[]} value={value} onValueChange={(next) => onValueChange(next)} openOnInputClick disabled={field.disabled}>
      <div className={s.box({ class: cn('pr-8', className) })}>
        <Autocomplete.Input
          id={field.id}
          aria-label={ariaLabel}
          aria-invalid={field.invalid || undefined}
          aria-describedby={field['aria-describedby']}
          placeholder={placeholder}
          maxLength={maxLength}
          // oxlint-disable-next-line jsx-a11y/no-autofocus -- opt-in prop for the first field of a panel opened by an explicit action
          autoFocus={autoFocus}
          className={s.input({ class: 't-field' })}
        />
        {options.length > 0 && (
          <Autocomplete.Trigger
            aria-label={placeholder ?? ariaLabel}
            className="flex size-20 shrink-0 cursor-pointer items-center justify-center rounded-4 text-icon outline-none hover:text-fg focus-visible:outline-2 focus-visible:outline-accent-2"
          >
            <RiArrowDownSLine aria-hidden className="size-16 transition-transform duration-150 group-has-[[data-popup-open]]/field:rotate-180" />
          </Autocomplete.Trigger>
        )}
      </div>
      <Autocomplete.Portal>
        <Autocomplete.Positioner sideOffset={4} align="start" className="z-50 outline-none">
          <Autocomplete.Popup
            className={cn(
              dropdownSurface,
              popupMotion,
              'max-h-[min(var(--available-height),20rem)] w-(--anchor-width) overflow-y-auto overscroll-contain data-empty:hidden',
            )}
          >
            {listLabel && <div className="px-8 pt-4 pb-2 t-caps">{listLabel}</div>}
            <Autocomplete.List className="flex flex-col gap-2">
              {(item: string) => (
                <Autocomplete.Item key={item} value={item} className={cn(dropdownItem, 'py-6')}>
                  <span className="truncate">{item}</span>
                </Autocomplete.Item>
              )}
            </Autocomplete.List>
          </Autocomplete.Popup>
        </Autocomplete.Positioner>
      </Autocomplete.Portal>
    </Autocomplete.Root>
  );
}
